import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import KanbanApi from 'dashboard/api/kanban';
import { CONVERSATION_STATUS } from 'shared/constants/messages';

/**
 * Kanban Store — visual CRM layer on top of native Chatwoot statuses.
 *
 * Configuration (pipelines + columns) is persisted in dedicated backend tables
 * (kanban_pipelines / kanban_columns) so it is SHARED across all agents of the
 * same account. Every column maps to exactly one native status
 * (open/pending/resolved/snoozed) — the column is a business/visual stage, the
 * native status remains the official operational state used by SLA, automations
 * and reports.
 *
 * Per-conversation column placement is stored on the native
 * `conversation.custom_attributes.kanban_column_id`, also shared across agents.
 *
 * Only the per-user UI collapse state stays in localStorage.
 */

const STORAGE_KEY_COLS_UI = 'crow_kanban_cols_ui';
// Legacy localStorage keys kept ONLY for one-time migration into the backend.
const LEGACY_KEY_COLS_CONFIG = 'crow_kanban_cols_config';
const LEGACY_KEY_CUSTOM_ASSIGNMENTS = 'crow_kanban_custom_assignments';
const CUSTOM_ATTR_COLUMN_KEY = 'kanban_column_id';

/**
 * NATIVE_STATUSES are the 4 statuses recognized by the Chatwoot backend.
 * Every column must declare a `nativeStatus` from this list — it determines
 * which backend status applies when a card is dropped into the column.
 */
export const NATIVE_STATUSES = [
  CONVERSATION_STATUS.PENDING,
  CONVERSATION_STATUS.OPEN,
  CONVERSATION_STATUS.SNOOZED,
  CONVERSATION_STATUS.RESOLVED,
];

function loadColsUiFromStorage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_COLS_UI);
    if (saved) return JSON.parse(saved);
  } catch {}
  return {};
}

function saveColsUiToStorage(ui) {
  try {
    localStorage.setItem(STORAGE_KEY_COLS_UI, JSON.stringify(ui));
  } catch {}
}

/**
 * Map a backend kanban_column payload to the flat shape the UI components use.
 * The `config` jsonb column stores the richer personalization (sort, rules,
 * agent assignment) so the relational schema stays lean.
 */
function mapBackendColumn(c) {
  const cfg = c.config || {};
  return {
    id: c.id,
    pipelineId: c.pipeline_id,
    name: c.name,
    color: c.color || '#6366F1',
    position: c.position ?? 0,
    nativeStatus: c.native_status,
    wipLimit: c.wip_limit ?? null,
    system: c.system === true,
    sortConfig: cfg.sortConfig || {
      type: 'manual',
      direction: 'asc',
      autoSort: false,
    },
    rules: cfg.rules || [],
    agentConfig: cfg.agentConfig || {
      assignmentMode: 'none',
      defaultAgentId: null,
      agentIds: [],
    },
  };
}

/**
 * Build the backend payload from a flat UI column. The personalization fields
 * are folded back into the `config` jsonb.
 */
function toBackendColumn(col) {
  return {
    name: col.name,
    native_status: col.nativeStatus,
    position: col.position,
    color: col.color,
    wip_limit: col.wipLimit,
    config: {
      sortConfig: col.sortConfig,
      rules: col.rules,
      agentConfig: col.agentConfig,
    },
  };
}

export const useKanbanStore = defineStore('kanban', () => {
  // conversations per status: { pending: [...], open: [...], snoozed: [...], resolved: [...] }
  const conversationsByStatus = reactive({
    [CONVERSATION_STATUS.PENDING]: [],
    [CONVERSATION_STATUS.OPEN]: [],
    [CONVERSATION_STATUS.SNOOZED]: [],
    [CONVERSATION_STATUS.RESOLVED]: [],
  });

  // loading flags per status
  const loadingByStatus = reactive({
    [CONVERSATION_STATUS.PENDING]: false,
    [CONVERSATION_STATUS.OPEN]: false,
    [CONVERSATION_STATUS.SNOOZED]: false,
    [CONVERSATION_STATUS.RESOLVED]: false,
  });

  // column UI state (collapsed) — per-user only, kept in localStorage.
  const colsUi = reactive(loadColsUiFromStorage());

  // Pipelines loaded from the backend (shared across all agents of the account).
  // Each pipeline embeds its columns. The active pipeline drives the board.
  const pipelines = ref([]);
  const activePipelineId = ref(null);
  const pipelinesLoaded = ref(false);

  const activePipeline = computed(
    () => pipelines.value.find(p => p.id === activePipelineId.value) || null
  );

  // colsConfig is derived (read-only) from the active pipeline's columns.
  const colsConfig = computed(() =>
    activePipeline.value ? [...activePipeline.value.columns] : []
  );

  // Per-conversation column placement cache: { [conversationId]: columnId }.
  // Hydrated from conversation.custom_attributes.kanban_column_id and used for
  // optimistic UI updates. Persisted back to the backend via custom_attributes.
  const customAssignments = reactive({});

  // drag state
  const dragState = reactive({
    draggingCardId: null,
    overColumnId: null,
  });

  // rules engine interval handle
  const rulesIntervalId = ref(null);

  // round-robin counters per column id
  const rrCounters = reactive({});

  // columns with merged UI state (from colsConfig)
  const columns = computed(() =>
    [...colsConfig.value]
      .sort((a, b) => a.position - b.position)
      .map(col => ({
        ...col,
        isCollapsed: colsUi[col.id]?.isCollapsed ?? false,
      }))
  );

  // ─── Backend config loading ──────────────────────────────────────────────

  function _ingestPipelines(payload) {
    pipelines.value = (payload || []).map(p => ({
      id: p.id,
      name: p.name,
      position: p.position ?? 0,
      columns: (p.columns || []).map(mapBackendColumn),
    }));
    if (
      !activePipelineId.value ||
      !pipelines.value.some(p => p.id === activePipelineId.value)
    ) {
      activePipelineId.value = pipelines.value[0]?.id ?? null;
    }
  }

  async function loadPipelines() {
    const response = await KanbanApi.getPipelines();
    _ingestPipelines(response?.data || []);
    pipelinesLoaded.value = true;
    clearLegacyStorage();
    return pipelines.value;
  }

  // Loads pipelines at most once. Used by the conversation list (ConversationCard)
  // so the column color side-border shows even when the Kanban board was never
  // opened in this session. Concurrent callers share the same in-flight promise.
  let _pipelinesPromise = null;
  function ensurePipelinesLoaded() {
    if (pipelinesLoaded.value) return Promise.resolve(pipelines.value);
    if (!_pipelinesPromise) {
      _pipelinesPromise = loadPipelines().finally(() => {
        _pipelinesPromise = null;
      });
    }
    return _pipelinesPromise;
  }

  function setActivePipeline(pipelineId) {
    if (pipelines.value.some(p => p.id === pipelineId)) {
      activePipelineId.value = pipelineId;
    }
  }

  /**
   * Find the column a conversation effectively belongs to in the active
   * pipeline. Honours an explicit assignment only when its column still maps to
   * the conversation's current native status; otherwise falls back to the FIRST
   * column mapping that status (covers status changes made outside the Kanban).
   */
  function effectiveColumnId(conversation) {
    if (!conversation) return null;
    const cols = colsConfig.value;
    const assigned = customAssignments[conversation.id];
    if (assigned != null) {
      const col = cols.find(c => c.id === assigned);
      if (col && col.nativeStatus === conversation.status) return col.id;
    }
    const first = cols
      .slice()
      .sort((a, b) => a.position - b.position)
      .find(c => c.nativeStatus === conversation.status);
    return first ? first.id : null;
  }

  function getConversationsForColumn(columnId) {
    const col = colsConfig.value.find(c => c.id === columnId);
    if (!col) return [];
    const bucket = conversationsByStatus[col.nativeStatus] || [];
    return bucket.filter(c => effectiveColumnId(c) === columnId);
  }

  // Limite total de conversas carregadas por status (paginadas).
  // Chatwoot retorna ~25 conversas por página; com 200 chegamos a ~8 páginas no pior caso.
  const MAX_CONVERSATIONS_PER_STATUS = 200;

  async function fetchColumnConversations(status, assigneeType = 'all') {
    loadingByStatus[status] = true;
    try {
      const collected = [];
      let page = 1;
      // Loop paginando até atingir o limite ou esvaziar
      // (proteção contra loops infinitos: hard cap de 20 páginas)
      while (collected.length < MAX_CONVERSATIONS_PER_STATUS && page <= 20) {
        const response = await ConversationApi.get({
          status,
          assigneeType,
          page,
        });
        const payload = response?.data?.data?.payload || [];
        if (payload.length === 0) break;
        collected.push(...payload);
        // Se a página voltou com menos de 25, é a última
        if (payload.length < 25) break;
        page += 1;
      }
      conversationsByStatus[status] = collected.slice(0, MAX_CONVERSATIONS_PER_STATUS);
      // Hydrate column placement from the conversation's custom_attributes so
      // the same layout is shown to every agent.
      conversationsByStatus[status].forEach(conv => {
        const colId = conv?.custom_attributes?.[CUSTOM_ATTR_COLUMN_KEY];
        if (colId != null) customAssignments[conv.id] = Number(colId);
      });
    } catch {
      conversationsByStatus[status] = [];
    } finally {
      loadingByStatus[status] = false;
    }
  }

  async function fetchAllColumns(assigneeType = 'all') {
    await Promise.all(
      Object.values(CONVERSATION_STATUS).map(s =>
        fetchColumnConversations(s, assigneeType)
      )
    );
  }

  /**
   * Persist a conversation's column placement on its native custom_attributes
   * (read-merge-write so we never clobber other attributes). Shared across
   * agents. Best-effort: failures don't block the optimistic UI move.
   */
  function persistColumnAssignment(conversation, columnId) {
    const merged = {
      ...(conversation.custom_attributes || {}),
      [CUSTOM_ATTR_COLUMN_KEY]: columnId,
    };
    // keep the in-memory object in sync so re-reads are consistent
    conversation.custom_attributes = merged;
    return ConversationApi.updateCustomAttributes({
      conversationId: conversation.id,
      customAttributes: merged,
    }).catch(() => {
      /* best-effort — placement also lives in customAssignments cache */
    });
  }

  /**
   * Move a conversation to a different column.
   *
   * Decision matrix (rules #4 and #5):
   *  - target column maps to a DIFFERENT native status → sync native status via
   *    toggleStatus (the official operational state changes).
   *  - target column maps to the SAME native status → only the visual stage
   *    changes; native status is untouched.
   *
   * Either way the placement is recorded on custom_attributes so it is shared.
   * Update is optimistic with rollback on the status API failure.
   */
  function moveCard(conversation, fromColumnId, toColumnId) {
    if (fromColumnId === toColumnId) return;

    const fromCol = colsConfig.value.find(c => c.id === fromColumnId);
    const toCol = colsConfig.value.find(c => c.id === toColumnId);
    if (!toCol) return;

    const fromNative = fromCol?.nativeStatus || conversation.status;
    const toNative = toCol.nativeStatus;
    const needsBackendUpdate = fromNative !== toNative;

    // Find the live conversation object in the source native bucket
    const srcList = conversationsByStatus[fromNative];
    const idx = srcList ? srcList.findIndex(c => c.id === conversation.id) : -1;

    // Optimistic local move when crossing native buckets
    if (needsBackendUpdate) {
      if (idx !== -1) srcList.splice(idx, 1);
      const updatedConv = {
        ...conversation,
        status: toNative,
        movedToColumnAt: Math.floor(Date.now() / 1000),
      };
      conversationsByStatus[toNative].unshift(updatedConv);
      conversation = updatedConv;
    }

    // Record + persist the visual placement (shared across agents)
    customAssignments[conversation.id] = toColumnId;
    persistColumnAssignment(conversation, toColumnId);

    // Backend: only update if the underlying native status changes
    if (needsBackendUpdate) {
      ConversationApi.toggleStatus({
        conversationId: conversation.id,
        status: toNative,
      }).catch(() => {
        // Rollback on failure
        const tgtList = conversationsByStatus[toNative];
        const tgtIdx = tgtList.findIndex(c => c.id === conversation.id);
        if (tgtIdx !== -1) tgtList.splice(tgtIdx, 1);
        if (idx !== -1 && srcList) srcList.splice(idx, 0, conversation);
      });
    }

    // Auto-assign agent if target column has agentConfig
    const targetCol = colsConfig.value.find(c => c.id === toColumnId);
    if (targetCol?.agentConfig) {
      const { assignmentMode, defaultAgentId, agentIds } = targetCol.agentConfig;
      let assignedAgentId = null;

      if (assignmentMode === 'default' && defaultAgentId) {
        assignedAgentId = defaultAgentId;
      } else if (assignmentMode === 'round-robin' && agentIds?.length) {
        if (!rrCounters[toColumnId]) rrCounters[toColumnId] = 0;
        assignedAgentId = agentIds[rrCounters[toColumnId] % agentIds.length];
        rrCounters[toColumnId]++;
      }

      if (assignedAgentId) {
        ConversationApi.assignAgent({
          conversationId: conversation.id,
          agentId: assignedAgentId,
        }).catch(() => { /* silent fail — assignment is best-effort */ });
      }
    }
  }

  /**
   * Sync a conversation update received from the Chatwoot backend (websocket).
   * Moves the card to the matching native column locally without calling the
   * API. Preserves the custom-column assignment if the new status matches the
   * custom column's nativeStatus.
   */
  function syncConversationFromBackend(updatedConv) {
    if (!updatedConv?.id || !updatedConv.status) return;
    const newStatus = updatedConv.status;
    if (!NATIVE_STATUSES.includes(newStatus)) return;

    // Remove from any native bucket where it currently lives
    for (const status of NATIVE_STATUSES) {
      const list = conversationsByStatus[status];
      const i = list.findIndex(c => c.id === updatedConv.id);
      if (i !== -1) {
        if (status === newStatus) {
          // Already in correct bucket — just update fields
          list[i] = { ...list[i], ...updatedConv };
          return;
        }
        list.splice(i, 1);
      }
    }

    // Add to the new native bucket
    conversationsByStatus[newStatus].unshift({
      ...updatedConv,
      movedToColumnAt: Math.floor(Date.now() / 1000),
    });

    // If the conversation had a column assignment whose nativeStatus no longer
    // matches the backend status, clear the stale assignment.
    const customColId = customAssignments[updatedConv.id];
    if (customColId != null) {
      const customCol = colsConfig.value.find(c => c.id === customColId);
      if (!customCol || customCol.nativeStatus !== newStatus) {
        delete customAssignments[updatedConv.id];
      }
    }
  }

  /**
   * Return the column object that a conversation currently belongs to.
   * Used by ConversationCard to show the column color as a side border in the
   * Chatwoot inbox.
   */
  function getColumnForConversation(conversation) {
    if (!conversation) return null;
    const colId = effectiveColumnId(conversation);
    return colsConfig.value.find(col => col.id === colId) || null;
  }

  // ─── Column CRUD (backend-backed, shared across agents) ──────────────────

  async function updateColumn(updatedCol) {
    const pid = updatedCol.pipelineId || activePipelineId.value;
    if (!pid || updatedCol.id == null) return;
    const current = colsConfig.value.find(c => c.id === updatedCol.id) || {};
    const merged = { ...current, ...updatedCol };
    await KanbanApi.updateColumn(pid, updatedCol.id, toBackendColumn(merged));
    await loadPipelines();
  }

  async function addColumn(newCol = {}) {
    const pid = activePipelineId.value;
    if (!pid) return;
    const maxPos = colsConfig.value.reduce(
      (m, c) => Math.max(m, c.position),
      -1
    );
    const payload = toBackendColumn({
      name: 'Nova Coluna',
      color: '#6366F1',
      wipLimit: null,
      position: maxPos + 1,
      nativeStatus: CONVERSATION_STATUS.OPEN,
      sortConfig: { type: 'manual', direction: 'asc', autoSort: false },
      rules: [],
      agentConfig: { assignmentMode: 'none', defaultAgentId: null, agentIds: [] },
      ...newCol,
    });
    await KanbanApi.createColumn(pid, payload);
    await loadPipelines();
  }

  async function removeColumn(columnId) {
    const pid = activePipelineId.value;
    if (!pid || columnId == null) return;
    // System (native) columns are protected and cannot be removed.
    const col = colsConfig.value.find(c => c.id === columnId);
    if (col?.system) return;
    await KanbanApi.deleteColumn(pid, columnId);
    await loadPipelines();
  }

  async function reorderColumns(orderedIds) {
    const pid = activePipelineId.value;
    if (!pid) return;
    await KanbanApi.reorderColumns(pid, orderedIds);
    await loadPipelines();
  }

  // ─── Pipeline CRUD ────────────────────────────────────────────────────────

  async function createPipeline(name) {
    const maxPos = pipelines.value.reduce((m, p) => Math.max(m, p.position), -1);
    const response = await KanbanApi.createPipeline({
      name: name || 'Novo Pipeline',
      position: maxPos + 1,
    });
    await loadPipelines();
    const created = response?.data;
    if (created?.id) activePipelineId.value = created.id;
    return created;
  }

  async function updatePipeline(pipelineId, attrs) {
    await KanbanApi.updatePipeline(pipelineId, attrs);
    await loadPipelines();
  }

  async function deletePipeline(pipelineId) {
    await KanbanApi.deletePipeline(pipelineId);
    if (activePipelineId.value === pipelineId) activePipelineId.value = null;
    await loadPipelines();
  }

  /**
   * Clean start: discard any legacy localStorage Kanban configuration so the
   * backend (per-account, shared) becomes the single source of truth. We do NOT
   * migrate the old config — the user opted to start fresh.
   */
  function clearLegacyStorage() {
    try {
      localStorage.removeItem(LEGACY_KEY_COLS_CONFIG);
      localStorage.removeItem(LEGACY_KEY_CUSTOM_ASSIGNMENTS);
    } catch {}
  }

  // ─── Rules Engine ────────────────────────────────────────────────────────────

  function _evaluateCondition(conv, rule, columnId) {
    const nowSec = Math.floor(Date.now() / 1000);
    switch (rule.condition) {
      case 'time-in-column': {
        const movedAt = conv.movedToColumnAt || conv.created_at || 0;
        const elapsed = (nowSec - movedAt) / 60; // minutes
        return elapsed >= Number(rule.conditionValue || 0);
      }
      case 'no-response': {
        const lastActivity = conv.last_activity_at || 0;
        const elapsed = (nowSec - lastActivity) / 60;
        return elapsed >= Number(rule.conditionValue || 0);
      }
      case 'priority-level': {
        const priority = conv.priority || '';
        return priority === (rule.conditionValue || '').trim().toLowerCase();
      }
      case 'sla-breach':
        return !!conv.slaBreached;
      case 'keyword-match': {
        const content = conv.last_non_activity_message?.content?.toLowerCase() || '';
        return content.includes((rule.conditionValue || '').toLowerCase());
      }
      case 'unread-count':
        return (conv.unread_count || 0) >= Number(rule.conditionValue || 0);
      default:
        return false;
    }
  }

  function _executeAction(conv, rule, columnId) {
    switch (rule.action) {
      case 'move-to-column': {
        const targetStatus = (rule.actionValue || '').trim();
        if (targetStatus && targetStatus !== columnId) {
          moveCard(conv, columnId, targetStatus);
        }
        break;
      }
      case 'change-priority': {
        const convList = conversationsByStatus[columnId];
        const idx = convList.findIndex(c => c.id === conv.id);
        if (idx !== -1) {
          convList[idx] = { ...convList[idx], priority: rule.actionValue };
        }
        break;
      }
      case 'add-tag': {
        const convList = conversationsByStatus[columnId];
        const idx = convList.findIndex(c => c.id === conv.id);
        if (idx !== -1) {
          const existing = convList[idx].labels || [];
          const tag = (rule.actionValue || '').trim().toLowerCase();
          if (tag && !existing.includes(tag)) {
            convList[idx] = { ...convList[idx], labels: [...existing, tag] };
          }
        }
        break;
      }
      case 'notify':
        // eslint-disable-next-line no-console
        console.log(`[KanbanRule] ${rule.name}: Conversa #${conv.id} —`, rule.actionValue);
        break;
      default:
        break;
    }
  }

  function processRules() {
    for (const col of colsConfig.value) {
      const activeRules = (col.rules || []).filter(r => r.enabled);
      if (!activeRules.length) continue;
      const convs = conversationsByStatus[col.id] || [];
      for (const conv of convs) {
        for (const rule of activeRules) {
          if (_evaluateCondition(conv, rule, col.id)) {
            _executeAction(conv, rule, col.id);
          }
        }
      }
    }
  }

  function startRulesEngine() {
    if (rulesIntervalId.value) return;
    rulesIntervalId.value = setInterval(processRules, 30_000);
  }

  function stopRulesEngine() {
    if (rulesIntervalId.value) {
      clearInterval(rulesIntervalId.value);
      rulesIntervalId.value = null;
    }
  }

  // ─── Export/Import config ─────────────────────────────────────────────────

  function exportConfig() {
    const data = {
      version: 1,
      exportedAt: new Date().toISOString(),
      columns: colsConfig.value,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kanban-config-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function importConfig(jsonString) {
    try {
      const data = JSON.parse(jsonString);
      const cols = data?.columns;
      if (!Array.isArray(cols) || !cols.length || !activePipelineId.value) {
        return Promise.resolve();
      }
      // Recreate the imported columns server-side so the config stays shared.
      const tasks = cols.map((c, idx) =>
        KanbanApi.createColumn(
          activePipelineId.value,
          toBackendColumn({
            name: c.name || 'Coluna',
            color: c.color || '#6366F1',
            position: c.position ?? idx,
            nativeStatus: c.nativeStatus || CONVERSATION_STATUS.OPEN,
            wipLimit: c.wipLimit ?? null,
            sortConfig: c.sortConfig || {
              type: 'manual',
              direction: 'asc',
              autoSort: false,
            },
            rules: c.rules || [],
            agentConfig: c.agentConfig || {
              assignmentMode: 'none',
              defaultAgentId: null,
              agentIds: [],
            },
          })
        ).catch(() => {})
      );
      return Promise.all(tasks).then(() => loadPipelines());
    } catch {
      return Promise.resolve();
    }
  }

  /**
   * Optimistic, in-memory only column reorder used for drag feedback. The order
   * is persisted to the backend separately via reorderColumns() on drop.
   */
  function reorderColumnsLocal(orderedIds) {
    const pipeline = activePipeline.value;
    if (!pipeline) return;
    orderedIds.forEach((id, position) => {
      const col = pipeline.columns.find(c => c.id === id);
      if (col) col.position = position;
    });
  }

  function toggleColumnCollapse(columnId) {
    if (!colsUi[columnId]) colsUi[columnId] = {};
    colsUi[columnId].isCollapsed = !colsUi[columnId].isCollapsed;
    saveColsUiToStorage({ ...colsUi });
  }

  function setDragCard(cardId) {
    dragState.draggingCardId = cardId;
  }

  function setDragOver(columnId) {
    dragState.overColumnId = columnId;
  }

  function clearDrag() {
    dragState.draggingCardId = null;
    dragState.overColumnId = null;
  }

  // Search filter across all conversations
  function filterConversations(searchQuery, statusFilter = '') {
    const q = searchQuery?.toLowerCase?.() || '';
    const sources = statusFilter
      ? { [statusFilter]: conversationsByStatus[statusFilter] }
      : conversationsByStatus;

    const result = {};
    for (const [status, list] of Object.entries(sources)) {
      if (!q) {
        result[status] = list;
        continue;
      }
      result[status] = list.filter(c => {
        const name = c.meta?.sender?.name?.toLowerCase() || '';
        const phone = c.meta?.sender?.phone_number?.toLowerCase() || '';
        const msg = c.last_non_activity_message?.content?.toLowerCase() || '';
        return name.includes(q) || phone.includes(q) || msg.includes(q);
      });
    }
    return result;
  }

  const isAnyLoading = computed(() =>
    Object.values(loadingByStatus).some(Boolean)
  );

  const totalCount = computed(() =>
    Object.values(conversationsByStatus).reduce((sum, l) => sum + l.length, 0)
  );

  return {
    columns,
    colsConfig,
    conversationsByStatus,
    customAssignments,
    loadingByStatus,
    isAnyLoading,
    totalCount,
    dragState,
    // pipelines
    pipelines,
    activePipeline,
    activePipelineId,
    pipelinesLoaded,
    loadPipelines,
    ensurePipelinesLoaded,
    setActivePipeline,
    createPipeline,
    updatePipeline,
    deletePipeline,
    getConversationsForColumn,
    getColumnForConversation,
    fetchColumnConversations,
    fetchAllColumns,
    moveCard,
    syncConversationFromBackend,
    toggleColumnCollapse,
    setDragCard,
    setDragOver,
    clearDrag,
    filterConversations,
    // column CRUD
    updateColumn,
    addColumn,
    removeColumn,
    reorderColumns,
    reorderColumnsLocal,
    // rules engine
    processRules,
    startRulesEngine,
    stopRulesEngine,
    // config import/export
    exportConfig,
    importConfig,
  };
});
